/**
 * §17 Integrations "error log" — every integration failure that still stands, from the two services
 * that own one: Tally postings the agent reported failed (TallySync), and IRN registrations and
 * e-way bills the government portals refused (Billing). Each service answers its own card; one
 * being unreachable leaves the other readable rather than blanking the page (constitution VI).
 *
 * Structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/index.tsx — a record
 * list under a page header — composed twice through `ReportTable`, not `PagedTable`: two server-paged
 * tables on one screen would both drive the one `?page=` param. Each card reads the newest 200 (the
 * services' own page cap) and says so when there are more, never implying it holds the whole log.
 *
 * Only the data, columns and labels are ours. Every error is the external system's own text, shown
 * verbatim (FR-039, FR-038) — never paraphrased.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link, useLoaderData, useOutletContext } from "react-router-dom";
import { EmptyState, ErrorSurface, VerbatimErrorText, getStatusBadge } from "@steelyard/theme";
import { ListHeader } from "../../components/table/list-header";
import { ReportTable } from "../../components/table/table-view";
import { can } from "../../lib/permissions";
import { formatMoment, listSyncErrors } from "../../lib/services/tallySync";
import { listIntegrationErrors, statusWords, type IntegrationErrorRow } from "../../lib/services/invoicing";
import type { AccountSummary, PermissionState } from "../../types";
import { invoicePath, syncItemPath } from "../../lib/paths";

/** The services' own page cap (`Math.Clamp(pageSize, 1, 200)` in both controllers). */
const NEWEST = 200;

export async function integrationErrorsLoader() {
  const [tally, billing] = await Promise.allSettled([
    listSyncErrors({ page: 1, pageSize: NEWEST }),
    listIntegrationErrors({ page: 1, pageSize: NEWEST }),
  ]);
  return { tally, billing };
}

type TallyRow = Awaited<ReturnType<typeof listSyncErrors>>["items"][number];

const tallyColumns: ColumnDef<TallyRow>[] = [
  { id: "lastAttemptAt", accessorKey: "lastAttemptAt", header: "Last attempt", size: 190, cell: ({ row }) => formatMoment(row.original.lastAttemptAt) },
  {
    id: "entityReference",
    accessorKey: "entityReference",
    header: "Record",
    size: 200,
    cell: ({ row }) => <Link to={syncItemPath(row.original.itemNumber || row.original.itemId)}>{row.original.entityReference}</Link>,
  },
  { id: "entityType", accessorKey: "entityType", header: "Type", size: 120, cell: ({ row }) => statusWords(row.original.entityType) },
  { id: "attempts", accessorKey: "attempts", header: "Attempts", size: 100, meta: { align: "right" } },
  {
    id: "lastError",
    header: "Tally's response",
    size: 420,
    cell: ({ row }) => <VerbatimErrorText label="Tally's response">{row.original.lastError ?? "No text reported."}</VerbatimErrorText>,
  },
];

const billingColumns: ColumnDef<IntegrationErrorRow>[] = [
  { id: "updatedAt", accessorKey: "updatedAt", header: "Last moved", size: 190, cell: ({ row }) => formatMoment(row.original.updatedAt) },
  {
    id: "invoiceNumber",
    accessorKey: "invoiceNumber",
    header: "Invoice",
    size: 200,
    cell: ({ row }) => <Link to={invoicePath(row.original.invoiceNumber)}>{row.original.invoiceNumber}</Link>,
  },
  { id: "customerName", accessorKey: "customerName", header: "Customer", size: 200 },
  { id: "state", accessorKey: "state", header: "State", size: 160, cell: ({ row }) => getStatusBadge(row.original.state) },
  {
    id: "irnError",
    header: "IRN portal's response",
    size: 320,
    cell: ({ row }) => (row.original.irnError ? <VerbatimErrorText label="IRN portal's response">{row.original.irnError}</VerbatimErrorText> : "—"),
  },
  {
    id: "ewayBillError",
    header: "E-way bill portal's response",
    size: 320,
    cell: ({ row }) =>
      row.original.ewayBillStatus === "failed" ? (
        <VerbatimErrorText label="E-way bill portal's response">{row.original.ewayBillError ?? "No text recorded."}</VerbatimErrorText>
      ) : (
        "—"
      ),
  },
];

/** "Showing the newest 200 of 350" — only when the service holds more than this card read. */
function more(shown: number, total: number) {
  return total > shown ? `Showing the newest ${shown} of ${total}.` : undefined;
}

export default function IntegrationErrorsPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const { tally, billing } = useLoaderData() as Awaited<ReturnType<typeof integrationErrorsLoader>>;

  if (!can(permissionState, "integrations.read")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "The integration error log is gated by integrations.read, which your role does not hold." }}
        action="view the integration error log"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Integration Errors"
        description="Failures that still stand, newest first, each in the external system's own words. A record leaves this log when its retry succeeds."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Integrations" }, { name: "Errors" }]}
      />

      {tally.status === "rejected" ? (
        <ErrorSurface error={tally.reason} action="read the Tally failures" />
      ) : tally.value.items.length === 0 ? (
        <EmptyState title="No failed Tally postings">Every record the agent reported on reached Tally.</EmptyState>
      ) : (
        <ReportTable
          title="Tally postings"
          description={more(tally.value.items.length, tally.value.totalCount)}
          rows={tally.value.items}
          columns={tallyColumns}
          getRowId={(row) => row.itemId}
          caption="Failed Tally postings: last attempt, record, type, attempts and Tally's response"
        />
      )}

      {billing.status === "rejected" ? (
        <ErrorSurface error={billing.reason} action="read the IRN and e-way bill failures" />
      ) : billing.value.items.length === 0 ? (
        <EmptyState title="No failed IRN or e-way bill">No registration or e-way bill failure stands on any invoice.</EmptyState>
      ) : (
        <ReportTable
          title="IRN and e-way bills"
          description={more(billing.value.items.length, billing.value.totalCount)}
          rows={billing.value.items}
          columns={billingColumns}
          getRowId={(row) => row.invoiceId}
          caption="Invoices with a failed IRN or e-way bill: when, invoice, customer, state and each portal's response"
        />
      )}
    </div>
  );
}
